/**
 * The speech bubble a first tap on a device shows (build decisions §3): one
 * component for the 2D map and the 3D building view. The renderers only say
 * where the device is now (`MapAnchorSource`); this rides with it, a frame at
 * a time, without a React render per frame.
 *
 *   Anchored   beside the device, with a tail.
 *   Docked     bottom left of the frame, when the device has no anchor to
 *              point at: on a cut-away floor (with *Show L6*), panned out of
 *              view, or lost.
 *   Strip      along the bottom on a phone, where the device keeps its ring
 *              on the map and a bubble would cover most of it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLifts } from '../../../api/hooks/useLifts';
import { DATA_TABLE_PHONE_QUERY } from '../../../design-system/components/DataTable';
import { useMediaQuery } from '../../../state/useMediaQuery';
import type { MapAnchor, MapAnchorSource, MapDevice } from '../../Map/map-bubble-anchor';
import { NO_ANCHOR } from '../../Map/map-bubble-anchor';
import {
  DoorBubbleBody,
  LiftBubbleBody,
  PeekWords,
  RobotBubbleBody,
  type RobotPresenceMap,
} from './bubble-contents';
import { BUBBLE_MARGIN_PX, BUBBLE_WIDTH_PX, inFrame, placeBubble } from './bubble-place';
import './MapBubble.css';

export function MapBubble({
  device,
  anchorSourceRef,
  frameRef,
  presenceMap,
  onClose,
  onDetails,
  onShowLevel,
}: {
  device: MapDevice;
  anchorSourceRef: { current: MapAnchorSource | null };
  /** The map frame the bubble stays inside. */
  frameRef: { current: HTMLElement | null };
  presenceMap: RobotPresenceMap;
  onClose: () => void;
  onDetails: () => void;
  onShowLevel: (level: string) => void;
}) {
  const strip = useMediaQuery(DATA_TABLE_PHONE_QUERY);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const tailRef = useRef<HTMLSpanElement>(null);
  /** Docked or not, and for which floor. A render only when this changes. */
  const [docked, setDocked] = useState<{ level: string | null } | null>(null);
  const dockedKey = useRef<string>('anchored');
  const lifts = useLifts().data;

  // Tapping anywhere outside the map closes it: the map's own taps come
  // through the renderer, where a tap on the device means "open the panel".
  useEffect(() => {
    const onPress = (e: PointerEvent) => {
      const target = e.target as Node;
      if (bubbleRef.current?.contains(target) || frameRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onPress);
    return () => document.removeEventListener('pointerdown', onPress);
  }, [frameRef, onClose]);

  useLayoutEffect(() => {
    if (strip) return;
    let raf = 0;
    const step = () => {
      raf = requestAnimationFrame(step);
      const frame = frameRef.current;
      const el = bubbleRef.current;
      if (!frame || !el) return;
      const f = frame.getBoundingClientRect();
      const anchor: MapAnchor = anchorSourceRef.current?.anchorOf(device) ?? NO_ANCHOR;
      const ax = anchor.state === 'visible' ? anchor.x - f.left : 0;
      const ay = anchor.state === 'visible' ? anchor.y - f.top : 0;
      const showing = anchor.state === 'visible' && inFrame(ax, ay, f.width, f.height);
      const key = showing ? 'anchored' : anchor.state === 'other-floor' ? `floor:${anchor.level}` : 'none';
      if (key !== dockedKey.current) {
        dockedKey.current = key;
        setDocked(showing ? null : { level: anchor.state === 'other-floor' ? anchor.level : null });
      }
      if (!showing) {
        el.style.left = `${BUBBLE_MARGIN_PX}px`;
        el.style.top = 'auto';
        el.style.bottom = `${BUBBLE_MARGIN_PX}px`;
        el.dataset.tail = 'none';
        return;
      }
      const placed = placeBubble({
        ax,
        ay,
        bw: el.offsetWidth,
        bh: el.offsetHeight,
        fw: f.width,
        fh: f.height,
      });
      el.style.left = `${placed.left}px`;
      el.style.top = `${placed.top}px`;
      el.style.bottom = 'auto';
      el.dataset.tail = placed.tail;
      el.style.setProperty('--nx-tail-at', `${placed.tailAt}px`);
    };
    step();
    return () => cancelAnimationFrame(raf);
  }, [strip, device, anchorSourceRef, frameRef]);

  const details = (
    <button
      type="button"
      className="nx-screen-btn nx-screen-btn--primary"
      onClick={onDetails}
      data-testid="bubble-details"
    >
      Details ›
    </button>
  );

  return (
    <div
      ref={bubbleRef}
      className={`nx-map-bubble${strip ? ' nx-map-bubble--strip' : ''}`}
      // Its own width on a laptop; a phone's strip is the frame's.
      style={strip ? undefined : { width: BUBBLE_WIDTH_PX }}
      role="dialog"
      aria-label={device.id}
      data-testid="map-bubble"
      data-kind={device.kind}
      data-docked={docked ? 'true' : undefined}
    >
      {strip && <span className="nx-map-bubble__grab" aria-hidden="true" />}
      <button
        type="button"
        className="nx-map-bubble__close"
        aria-label="Close"
        onClick={onClose}
        data-testid="bubble-close"
      >
        <span aria-hidden="true">×</span>
      </button>
      {docked && !strip && (
        <p className="nx-map-bubble__dock" data-testid="bubble-dock">
          {docked.level ? `On ${docked.level}, which is not showing.` : 'Not in view on this map.'}
          {docked.level && (
            <button
              type="button"
              className="nx-map-bubble__show"
              onClick={() => onShowLevel(docked.level!)}
              data-testid="bubble-show-level"
            >
              Show {docked.level}
            </button>
          )}
        </p>
      )}
      {device.kind === 'robot' && (
        <RobotBubbleBody name={device.id} presenceMap={presenceMap} lifts={lifts} footer={details} />
      )}
      {device.kind === 'lift' && (
        <LiftBubbleBody name={device.id} onRecover={onDetails} footer={details} />
      )}
      {device.kind === 'door' && <DoorBubbleBody name={device.id} footer={details} />}
      {!strip && <span ref={tailRef} className="nx-map-bubble__tail" aria-hidden="true" />}
    </div>
  );
}

/**
 * The one-line hover peek: mouse only, following the pointer. Its own
 * component, moved by hand, so a mouse crossing the map does not re-render
 * the Overview at every pixel.
 */
export function MapHoverPeek({
  device,
  peekRef,
  presenceMap,
}: {
  device: MapDevice | null;
  peekRef: { current: HTMLDivElement | null };
  presenceMap: RobotPresenceMap;
}) {
  return (
    <div
      ref={(el) => {
        peekRef.current = el;
      }}
      className="nx-map-peek"
      hidden={device === null}
      data-testid="map-peek"
    >
      {device && <PeekWords device={device} presenceMap={presenceMap} />}
    </div>
  );
}
